import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Space, Typography } from 'antd';

import { importCrosswordCompiler } from '../../../services/admin';
import { getModule, listModules } from './registry';
import type { ImportedState } from './types';
import type { CreationMeta } from '../catalog/apply-creation-meta';

// "Importar do Crossword Compiler" (spec §5.2): manda o XML ao servidor, que devolve o
// RASCUNHO (nada é gravado), converte com o `import.toDoc` do módulo e abre o editor
// de criação com ele. Só aparece em módulos que declaram `import`.
//
// Dentro do "+ Novo jogo" recebe `titulo` (sobrescreve o do arquivo quando preenchido)
// e `meta` (vai no router state; o shell grava depois do primeiro "Salvar").

interface ImportFailure {
  message: string;
  erros: string[];
}

export function importErrorMessage(e: unknown): ImportFailure {
  const response = (e as { response?: { status?: number; data?: { message?: unknown; msg?: unknown; erros?: unknown } } })
    ?.response;
  if (response?.status === 413) return { message: 'O arquivo passa de 1 MB.', erros: [] };
  const data = response?.data;
  const message =
    (typeof data?.message === 'string' && data.message) ||
    (typeof data?.msg === 'string' && data.msg) ||
    'Falha ao importar o arquivo.';
  const erros = Array.isArray(data?.erros) ? data!.erros.filter((x): x is string => typeof x === 'string') : [];
  return { message, erros };
}

/** Troca o `titulo` do doc importado. Todo doc de módulo importável tem `titulo`;
 *  um doc sem o campo passa intacto. */
function withTitle(doc: unknown, titulo: string | undefined): unknown {
  const trimmed = titulo?.trim();
  if (!trimmed || typeof doc !== 'object' || doc === null || !('titulo' in doc)) return doc;
  return { ...doc, titulo: trimmed };
}

interface ImportXmlCardProps {
  kind: string;
  titulo?: string;
  meta?: CreationMeta;
  /** Chamado logo antes de navegar para o editor (o wizard fecha o modal). */
  onNavigate?: () => void;
}

export const ImportXmlCard: React.FC<ImportXmlCardProps> = ({ kind, titulo, meta, onNavigate }) => {
  const navigate = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ImportFailure | null>(null);

  const module = getModule(kind);
  const importer = module?.import;
  if (!module || !importer) return null;

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setFailure(null);
    setBusy(true);
    // Após navegar o card é desmontado: não atualizar estado nesse caso.
    let navigated = false;
    try {
      const result = await importCrosswordCompiler(file);
      if (result.kind !== importer.kind) {
        const right = listModules().find((m) => m.import?.kind === result.kind);
        setFailure({
          message: right
            ? `Este arquivo é um ${right.label}. Selecione o tipo "${right.label}" e importe de novo.`
            : 'Este tipo de arquivo não é importado por aqui.',
          erros: [],
        });
        return;
      }
      const imported: ImportedState = {
        doc: withTitle(importer.toDoc(result), titulo),
        warnings: result.warnings,
      };
      navigated = true;
      onNavigate?.();
      navigate(`/admin/editor/${kind}`, { state: meta ? { imported, meta } : { imported } });
    } catch (e) {
      setFailure(importErrorMessage(e));
    } finally {
      if (!navigated) {
        setBusy(false);
        if (input.current) input.current.value = '';
      }
    }
  };

  return (
    <Card size="small" title="Importar do Crossword Compiler">
      <Space direction="vertical" style={{ width: '100%' }}>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Envie o XML exportado pelo Crossword Compiler. O jogo abre no editor para revisão — só é
          gravado quando você clicar em Salvar.
        </Typography.Paragraph>
        <input
          ref={input}
          type="file"
          accept=".xml"
          aria-label="Arquivo XML do Crossword Compiler"
          style={{ display: 'none' }}
          onChange={(e) => onFile(e.target.files?.[0])}
        />
        <Button loading={busy} onClick={() => input.current?.click()}>
          Importar XML
        </Button>
        {failure && (
          <Alert
            type="error"
            showIcon
            message={failure.message}
            description={
              failure.erros.length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {failure.erros.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              ) : undefined
            }
          />
        )}
      </Space>
    </Card>
  );
};

export default ImportXmlCard;
